import { useCallback, useRef, useState } from "react";
import { Move } from "lucide-react";

export function BeforeAfter({
  before,
  after,
  beforeSmall,
  afterSmall,
  alt,
}: {
  before: string;
  after: string;
  /** Narrow-viewport variants, served via srcSet so phones don't download the wide file. */
  beforeSmall?: string;
  afterSmall?: string;
  alt: string;
}) {
  // The slider is capped at ~900px wide by its container, so 800w covers phones and
  // 1400w covers tablet/desktop including 2x displays.
  const srcSetFor = (wide: string, small?: string) =>
    small ? `${small} 800w, ${wide} 1400w` : undefined;
  const SIZES = "(max-width: 768px) 100vw, 900px";
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = () => setDragging(false);

  return (
    <div
      ref={containerRef}
      className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-border shadow-luxe select-none touch-none"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <img
        src={after}
        srcSet={srcSetFor(after, afterSmall)}
        sizes={SIZES}
        alt={`${alt} (etter)`}
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
        loading="lazy"
        decoding="async"
      />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img
          src={before}
          srcSet={srcSetFor(before, beforeSmall)}
          sizes={SIZES}
          alt={`${alt} (før)`}
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className="pointer-events-none absolute top-4 left-4 rounded-full bg-background/80 backdrop-blur px-3 py-1 text-xs font-medium text-foreground">Før</div>
      <div className="pointer-events-none absolute top-4 right-4 rounded-full bg-background/80 backdrop-blur px-3 py-1 text-xs font-medium text-foreground">Etter</div>

      <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-primary" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-primary-strong text-primary-foreground shadow-luxe flex items-center justify-center">
          <Move className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}
